// API Setup Page - Stage 06 - Dashcam Provider Connection
// Features: Provider selection + step-by-step API token wizard
// Collects: API Token only (Organization ID collection removed Dec 2025)
// UPDATED: Navigate to schedule-demo after successful validation
// UPDATED: Uses PasswordInput component with visibility toggle
// UPDATED: Added demo mode support
// UPDATED: Removed Samsara branding, added provider dropdown (Apr 2026 - C&D compliance)
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboarding } from '../../context/OnboardingContext'
import { useAuth } from '../../context/AuthContext'
import { useAdmin } from '../../context/AdminContext'
import { useDemoMode } from '../../hooks/useDemoMode'
import { n8nApi } from '../../services/n8nApi'
import PasswordInput from '../../components/PasswordInput'

const SAMSARA_REGIONS = [
  { id: 'us', label: 'United States', url: 'api.samsara.com', dashboardUrl: 'https://cloud.samsara.com' },
  { id: 'eu', label: 'Europe', url: 'api.eu.samsara.com', dashboardUrl: 'https://cloud.eu.samsara.com' },
  { id: 'ca', label: 'Canada', url: 'api.ca.samsara.com', dashboardUrl: 'https://cloud.ca.samsara.com' }
]

const DASHCAM_PROVIDERS = [
  { id: 'samsara', label: 'Samsara' },
  { id: 'lytx', label: 'Lytx' },
  { id: 'motive', label: 'Motive' },
  { id: 'netradyne', label: 'Netradyne' }
]

// Step definitions with detailed instructions
const SETUP_STEPS = [
  {
    id: 1,
    title: 'Open Settings',
    shortTitle: 'Settings',
    instruction: 'In your dashcam provider dashboard, click the Settings icon in the bottom-left corner of the screen.',
    tip: 'Look for the gear icon (⚙️) in the navigation sidebar.',
    completionHint: 'Click "Done" once you\'re in the Settings menu'
  },
  {
    id: 2,
    title: 'Navigate to API Tokens',
    shortTitle: 'API Tokens',
    instruction: 'In Settings, find "Developer" in the left sidebar, then click "API Tokens".',
    tip: 'Developer settings are typically near the bottom of the Settings menu.',
    completionHint: 'Click "Done" when you see the API Tokens page'
  },
  {
    id: 3,
    title: 'Add New Token',
    shortTitle: 'Add Token',
    instruction: 'Click the "Add an API token" button in the upper right-hand corner to create a new token.',
    tip: 'This opens a dialog where you\'ll configure the token settings.',
    completionHint: 'Click "Done" when the token creation dialog opens'
  },
  {
    id: 4,
    title: 'Name Your Token',
    shortTitle: 'Name',
    instruction: 'Enter a name to identify this token. We recommend:',
    suggestedValue: 'FleetVision',
    tip: 'This name helps you identify the token\'s purpose later.',
    completionHint: 'Click "Done" after entering the token name'
  },
  {
    id: 5,
    title: 'Set Tag Access',
    shortTitle: 'Access',
    instruction: 'Select the Tag Access level. For the demo, select "Entire Organization".',
    tip: 'This determines which vehicles and drivers the token can access.',
    completionHint: 'Click "Done" after selecting tag access'
  },
  {
    id: 6,
    title: 'Set Permissions',
    shortTitle: 'Permissions',
    instruction: 'Scroll down to "Scopes" and enable these permissions:',
    permissionGroups: [
      {
        category: 'Setup & Administration',
        permissions: ['Read Org Information']
      },
      {
        category: 'Trips',
        permissions: ['Read Trips']
      },
      {
        category: 'Vehicles',
        permissions: ['Read Vehicles', 'Read Vehicle Trips']
      },
      {
        category: 'Drivers',
        permissions: ['Read Drivers']
      },
      {
        category: 'Safety & Cameras',
        permissions: ['Read Camera Media', 'Read Safety Events & Scores']
      }
    ],
    tip: 'You only need Read permissions—the demo doesn\'t modify any data.',
    completionHint: 'Click "Done" after selecting the permissions'
  },
  {
    id: 7,
    title: 'Save & Copy Token',
    shortTitle: 'Save',
    instruction: 'Click "Save" to create the token. IMPORTANT: Select "Copy to clipboard" to copy the token string immediately and save it somewhere safe where you can easily find it again — you won\'t be able to see it again!',
    tip: 'The token is a long alphanumeric string — copy the whole value, including any prefix.',
    completionHint: 'Click "Done" after copying the token to your clipboard',
    isFinal: true
  }
]

// Checklist step component
const ChecklistStep = ({
  step,
  isActive,
  isCompleted,
  onComplete,
  isLast
}) => {
  return (
    <div className={`
      relative pl-8 pb-6
      ${!isLast ? 'border-l-2 ml-3' : 'ml-3'}
      ${isCompleted ? 'border-brand-300' : isActive ? 'border-brand-400' : 'border-slate-200'}
    `}>
      {/* Step number circle */}
      <div className={`
        absolute -left-3 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
        transition-all duration-300
        ${isCompleted
          ? 'bg-accent-500 text-white'
          : isActive
            ? 'bg-brand-600 text-white ring-4 ring-brand-100'
            : 'bg-slate-200 text-slate-500'
        }
      `}>
        {isCompleted ? (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        ) : step.id}
      </div>

      {/* Step content */}
      <div className={`
        rounded-lg p-4 transition-all duration-300
        ${isActive ? 'bg-brand-50 border border-brand-200' : isCompleted ? 'bg-slate-50' : 'opacity-60'}
      `}>
        <h3 className={`font-semibold mb-2 ${isActive ? 'text-brand-900' : 'text-slate-700'}`}>
          {step.title}
        </h3>

        {(isActive || isCompleted) && (
          <div className="space-y-3 animate-fade-in">
            <p className="text-sm text-slate-600">{step.instruction}</p>

            {step.suggestedValue && (
              <div className="flex items-center gap-2">
                <code className="px-3 py-1.5 bg-white border border-slate-200 rounded text-sm font-mono text-brand-700">
                  {step.suggestedValue}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(step.suggestedValue)}
                  className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </button>
              </div>
            )}

            {step.permissionGroups && (
              <div className="space-y-3">
                {step.permissionGroups.map((group, groupIdx) => (
                  <div key={groupIdx}>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      {group.category}
                    </p>
                    <ul className="space-y-1">
                      {group.permissions.map((perm, permIdx) => (
                        <li key={permIdx} className="flex items-center gap-2 text-sm text-slate-700">
                          <svg className="w-4 h-4 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                          </svg>
                          {perm}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {step.tip && (
              <p className="text-xs text-slate-500 flex items-start gap-1.5">
                <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{step.tip}</span>
              </p>
            )}

            {/* Done button */}
            {isActive && !isCompleted && (
              <button
                onClick={onComplete}
                className="mt-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
              >
                {step.isFinal ? "I've copied the token" : 'Done - Next Step'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Wizard + Token Entry (shared by both prospect and admin) ─────────────────
const SetupWizard = ({
  region, setRegion, apiToken, setApiToken,
  isValidating, validationError, setValidationError, validationSuccess,
  handleValidateToken, handleTokenPaste, tokenInputRef,
  currentStep, setCurrentStep, completedSteps, setCompletedSteps,
  handleStepComplete, isAdmin
}) => {
  return (
    <>
      {/* Step 0: Region Selection */}
      {currentStep === 0 && (
        <div className="card max-w-xl mx-auto animate-slide-up">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            {isAdmin ? "Select the prospect's region" : 'Select your region'}
          </h2>
          <p className="text-slate-600 mb-6">
            This helps us connect to the right server for your account.
          </p>

          <div className="space-y-3 mb-6">
            {SAMSARA_REGIONS.map((r) => (
              <label
                key={r.id}
                className={`
                  flex items-center p-4 border rounded-lg cursor-pointer transition-all
                  ${region === r.id
                    ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                    : 'border-slate-200 hover:border-slate-300'
                  }
                `}
              >
                <input
                  type="radio"
                  name="region"
                  value={r.id}
                  checked={region === r.id}
                  onChange={(e) => setRegion(e.target.value)}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                />
                <div className="ml-3">
                  <span className="font-medium text-slate-900">{r.label}</span>
                </div>
              </label>
            ))}
          </div>

          <button
            onClick={() => setCurrentStep(1)}
            className="btn-primary w-full"
          >
            Continue
          </button>
        </div>
      )}

      {/* Steps 1-7: Interactive Checklist */}
      {currentStep >= 1 && currentStep <= SETUP_STEPS.length && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Checklist */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Setup Checklist</h2>
            </div>

            <div className="space-y-0">
              {SETUP_STEPS.map((step, index) => (
                <ChecklistStep
                  key={step.id}
                  step={step}
                  isActive={currentStep === step.id}
                  isCompleted={completedSteps.has(step.id)}
                  onComplete={() => handleStepComplete(step.id)}
                  isLast={index === SETUP_STEPS.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Right: Progress & Help */}
          <div className="space-y-6">
            {/* Progress card */}
            <div className="card bg-gradient-to-br from-brand-50 to-white">
              <h3 className="font-semibold text-slate-900 mb-3">Your Progress</h3>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-600 rounded-full transition-all duration-500"
                    style={{ width: `${(completedSteps.size / SETUP_STEPS.length) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-slate-700">
                  {completedSteps.size}/{SETUP_STEPS.length}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                {completedSteps.size === 0
                  ? 'Follow the steps in your dashcam provider dashboard'
                  : completedSteps.size === SETUP_STEPS.length
                    ? 'All steps complete! Paste your token below.'
                    : `${SETUP_STEPS.length - completedSteps.size} steps remaining`
                }
              </p>
            </div>

            {/* Help card */}
            <div className="card">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Need Help?
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>{isAdmin ? 'The prospect must' : 'You must'} be a <strong>Full Admin</strong> or <strong>Standard Admin</strong> to create API tokens</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>The token is shown only once—make sure to copy it immediately after clicking Save</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400">•</span>
                  <span>If something goes wrong, you can always regenerate the token in your dashcam provider dashboard</span>
                </li>
              </ul>
            </div>

            {/* Security note */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-700 mb-1">Your data is secure</p>
                  <p>Your API token is transmitted securely via HTTPS and is used only to generate your demo. Per our agreement, all access expires after 30 days.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final Step: Token Entry */}
      {currentStep > SETUP_STEPS.length && (
        <div className="card max-w-xl mx-auto animate-slide-up">
          <div className="text-center mb-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Almost There!</h2>
            <p className="text-slate-600 mt-1">Paste your API token below to connect</p>
          </div>

          {/* Summary of collected data */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-600 mb-2">Connecting with:</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Region:</span>
                <span className="font-medium text-slate-900">
                  {SAMSARA_REGIONS.find(r => r.id === region)?.label}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="apiToken" className="label">
              API Token
            </label>
            <PasswordInput
              id="apiToken"
              name="apiToken"
              autoComplete="off"
              ref={tokenInputRef}
              value={apiToken}
              onChange={(e) => {
                setApiToken(e.target.value)
                setValidationError('')
              }}
              onPaste={handleTokenPaste}
              error={!!validationError}
              className={`font-mono ${validationSuccess ? 'border-accent-300' : ''}`}
              placeholder="Paste your API token here"
            />

            {validationError && (
              <p className="mt-2 text-sm text-red-600 flex items-start gap-1.5">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {validationError}
              </p>
            )}

            {validationSuccess && (
              <p className="mt-2 text-sm text-accent-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Token validated! Redirecting to schedule your demo...
              </p>
            )}
          </div>

          <button
            onClick={handleValidateToken}
            className="btn-secondary w-full"
            disabled={isValidating || !apiToken.trim() || validationSuccess}
          >
            {isValidating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Validating...
              </span>
            ) : validationSuccess ? (
              <span className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Connected!
              </span>
            ) : (
              'Validate & Connect'
            )}
          </button>

          {/* Back button */}
          <button
            onClick={() => {
              setCurrentStep(1)
              setCompletedSteps(new Set())
              setApiToken('')
              setValidationError('')
            }}
            className="mt-4 w-full text-sm text-slate-600 hover:text-slate-800"
          >
            ← Start over
          </button>
        </div>
      )}
    </>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
const APISetupPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isImpersonating } = useAdmin()
  const { transitionToStage, updateProspectData, STAGES, effectiveUid, actingUid } = useOnboarding()
  const { isDemoMode, simulateSuccess } = useDemoMode()

  // State
  const [selectedProvider, setSelectedProvider] = useState('')
  const [region, setRegion] = useState('us')
  const [currentStep, setCurrentStep] = useState(0) // 0 = region selection, 1-7 = checklist steps
  const [completedSteps, setCompletedSteps] = useState(new Set())
  const [apiToken, setApiToken] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [validationSuccess, setValidationSuccess] = useState(false)

  const tokenInputRef = useRef(null)

  // Focus token input when reaching final step
  useEffect(() => {
    if (currentStep === SETUP_STEPS.length + 1 && tokenInputRef.current) {
      tokenInputRef.current.focus()
    }
  }, [currentStep])

  const handleStepComplete = (stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]))

    if (stepId < SETUP_STEPS.length) {
      setCurrentStep(stepId + 1)
    } else {
      // All steps complete, show token input
      setCurrentStep(SETUP_STEPS.length + 1)
    }
  }

  const handleValidateToken = async () => {
    if (!apiToken.trim()) {
      setValidationError('Please paste your API token')
      return
    }

    // Basic format validation (skip in demo mode)
    if (!isDemoMode && !apiToken.startsWith('samsara_api_')) {
      setValidationError('Please check you copied the full token.')
      return
    }

    setIsValidating(true)
    setValidationError('')
    setValidationSuccess(false)

    // DEMO MODE: Simulate successful validation
    if (isDemoMode) {
      simulateSuccess(() => {
        setIsValidating(false)
        setValidationSuccess(true)

        // Simulate transition after brief pause
        setTimeout(async () => {
          await transitionToStage(STAGES.API_CONNECTED)
          navigate('/onboarding/schedule-demo')
        }, 1500)
      }, 1500)
      return
    }

    try {
      // Use effectiveUid for the prospect being assisted
      // Pass actingUid for audit trail when impersonating
      const response = await n8nApi.validateSamsaraToken(
        effectiveUid,
        apiToken,
        region,
        { acting: actingUid }
      )

      if (response.success) {
        setValidationSuccess(true)

        // Update local state
        updateProspectData({
          samsaraRegion: region,
          samsaraConnected: true
        })

        // Transition after brief pause
        setTimeout(async () => {
          const success = await transitionToStage(STAGES.API_CONNECTED)
          if (success) {
            navigate('/onboarding/schedule-demo')
          }
        }, 1500)

      } else {
        const errorMessages = {
          'invalid_token': 'Invalid API token. Please check you copied the complete token.',
          'authentication_failed': 'Authentication failed. The token may be incorrect or expired.',
          'insufficient_permissions': response.error || 'This token doesn\'t have the required permissions.',
          'region_mismatch': 'This token doesn\'t match the selected region. Please verify the region.',
          'rate_limited': 'Too many attempts. Please wait a moment and try again.',
          'unknown': response.error || 'Validation failed. Please try again.'
        }
        setValidationError(errorMessages[response.error_type] || errorMessages.unknown)
      }

    } catch (error) {
      console.error('Token validation error:', error)
      setValidationError('Connection error. Please check your internet and try again.')
    } finally {
      setIsValidating(false)
    }
  }

  // Handle paste event
  const handleTokenPaste = (e) => {
    e.preventDefault()
    const pastedText = e.clipboardData?.getData('text') || ''
    setApiToken(pastedText.trim())
    setValidationError('')
  }

  // Shared wizard props
  const wizardProps = {
    region, setRegion, apiToken, setApiToken,
    isValidating, validationError, setValidationError, validationSuccess,
    handleValidateToken, handleTokenPaste, tokenInputRef,
    currentStep, setCurrentStep, completedSteps, setCompletedSteps,
    handleStepComplete
  }

  // ─── Admin view: go straight to wizard (no provider selection) ──────────────
  if (isImpersonating) {
    return (
      <div className="max-w-4xl mx-auto animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">
            Connect Dashcam Provider Account
          </h1>
          <p className="mt-3 text-slate-600">
            Walk the prospect through creating an API token in their dashcam provider dashboard. This takes about 2 minutes.
          </p>
        </div>
        <SetupWizard {...wizardProps} isAdmin={true} />
      </div>
    )
  }

  // ─── Prospect view: provider selection → wizard ─────────────────────────────
  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">
          Connect Your Dashcam Provider
        </h1>
        <p className="mt-3 text-slate-600">
          {selectedProvider === 'samsara' && currentStep > 0
            ? "Follow the steps below to create an API token. This takes about 2 minutes."
            : "Select your dashcam provider and enter your API credentials to generate your personalised demo."
          }
        </p>
      </div>

      {/* Provider Selection — shown until wizard starts */}
      {(currentStep === 0 || !selectedProvider) && (
        <div className="card max-w-xl mx-auto mb-6 animate-slide-up">
          <label className="label mb-3">Select your dashcam provider</label>
          <div className="space-y-2">
            {DASHCAM_PROVIDERS.map((provider) => (
              <label
                key={provider.id}
                className={`
                  flex items-center p-3 border rounded-lg cursor-pointer transition-all
                  ${selectedProvider === provider.id
                    ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                    : 'border-slate-200 hover:border-slate-300'
                  }
                `}
              >
                <input
                  type="radio"
                  name="provider"
                  value={provider.id}
                  checked={selectedProvider === provider.id}
                  onChange={(e) => {
                    setSelectedProvider(e.target.value)
                    setCurrentStep(0)
                    setCompletedSteps(new Set())
                    setApiToken('')
                    setValidationError('')
                  }}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                />
                <span className="ml-3 font-medium text-slate-900">{provider.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Samsara selected → show wizard */}
      {selectedProvider === 'samsara' && (
        <SetupWizard {...wizardProps} isAdmin={false} />
      )}

      {/* Stub for unsupported providers */}
      {selectedProvider && selectedProvider !== 'samsara' && (
        <div className="card max-w-xl mx-auto animate-slide-up text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            {DASHCAM_PROVIDERS.find(p => p.id === selectedProvider)?.label} Integration Coming Soon
          </h2>
          <p className="text-slate-600 mb-6">
            We're actively building support for {DASHCAM_PROVIDERS.find(p => p.id === selectedProvider)?.label}.
            Our team will be in touch when your integration is ready.
          </p>
          <p className="text-sm text-slate-500">
            In the meantime, you can schedule a call with our team to discuss your setup.
          </p>
        </div>
      )}
    </div>
  )
}

export default APISetupPage
